import { z } from 'zod';
export const AccessoryCutRules = z.object({
    safety_m: z.number().nonnegative().max(1000).nullable(),
    min_join_m: z.number().positive().max(10).nullable(),
    min_reusable_offcut_m: z.number().nonnegative().max(10).nullable(),
});
export const AccessoryMaterialPlan = z.object({
    version: z.literal(1),
    kind: z.enum(['scotia', 'trims']),
    status: z.enum(['resolved', 'unresolved']),
    issues: z.array(z.string()).max(20),
    rule_version: z.number().int().positive(),
    product_id: z.string().max(80),
    variant_id: z.string().max(160),
    supplier_id: z.string().max(80),
    supplier_name: z.string().max(160),
    product_name: z.string().max(200),
    variant_name: z.string().max(240),
    source_document: z.object({ id: z.string().max(80).nullable(), name: z.string().max(240).nullable(), effective_date: z.string().max(20).nullable() }).nullable(),
    runs: z.array(z.object({ id: z.string().max(160), label: z.string().max(240), length_m: z.number().positive().max(10000) })).max(1000),
    measured_lm: z.number().nonnegative().max(100000),
    safety_lm: z.number().nonnegative().max(1000).nullable(),
    stock_piece_length_m: z.number().positive().max(100).nullable(),
    pieces: z.number().int().nonnegative().max(100000).nullable(),
    purchased_lm: z.number().nonnegative().max(1000000).nullable(),
    quoted: z.object({ unit: z.string().max(20), quantity: z.number().nonnegative().max(1e8), rate: z.number().nonnegative().max(1e8).nullable() }),
});
export const AccessoryMaterialSnapshot = z.object({ version: z.literal(1), plans: z.array(AccessoryMaterialPlan).max(60) });
/**
 * First-fit decreasing stock calculation. Runs may share reusable offcuts, but a run is split only when
 * both sides of the join satisfy the configured minimum. Safety is applied once to the combined order,
 * never once per room. Millimetres are used internally so floating point cannot lose a piece at a boundary.
 */
export function cutAccessoryPieces(runsM, stockM, rules) {
    const parsed = AccessoryCutRules.parse(rules);
    if (parsed.safety_m === null || parsed.min_join_m === null || parsed.min_reusable_offcut_m === null || stockM <= 0)
        return null;
    const mm = (m) => Math.round(m * 1000), stock = mm(stockM), minJoin = mm(parsed.min_join_m), reusable = mm(parsed.min_reusable_offcut_m);
    if (stock <= 0 || minJoin > stock)
        return null;
    const remainders = [];
    const open = () => { remainders.push(stock); return remainders.length - 1; };
    const place = (segment) => {
        let index = -1;
        for (let i = 0; i < remainders.length; i++)
            if (remainders[i] >= segment && (index < 0 || remainders[i] < remainders[index]))
                index = i;
        if (index < 0)
            index = open();
        remainders[index] = remainders[index] - segment;
    };
    for (const raw of [...runsM].filter(n => n > 0).sort((a, b) => b - a)) {
        let remaining = mm(raw);
        while (remaining > stock) {
            const segment = remaining - stock < minJoin ? remaining - minJoin : stock;
            if (segment < minJoin || segment > stock)
                return null;
            place(segment);
            remaining -= segment;
        }
        if (remaining > 0)
            place(remaining);
    }
    const required = mm(runsM.reduce((n, v) => n + Math.max(0, v), 0) + (parsed.safety_m ?? 0));
    while (remainders.length * stock < required)
        open();
    // Offcuts below the reusable threshold are deliberately waste; they are never offered to another run.
    for (let i = 0; i < remainders.length; i++)
        if (remainders[i] < reusable)
            remainders[i] = 0;
    return { pieces: remainders.length, purchased_m: Math.round(remainders.length * stock) / 1000 };
}
//# sourceMappingURL=accessory-materials.js.map